import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CreateRequestRequest,
  PagedResult,
  RequestDto,
  RequestExportLayout,
  RequestListRequest,
  UpdateRequestRequest,
} from '../models/request.models';

@Injectable({
  providedIn: 'root',
})
export class RequestService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/requests`;

  getRequests(
    requestParams: RequestListRequest,
  ): Observable<PagedResult<RequestDto>> {
    let params = new HttpParams()
      .set('page', requestParams.page.toString())
      .set('pageSize', requestParams.pageSize.toString());

    if (requestParams.search) {
      params = params.set('search', requestParams.search);
    }

    if (
      requestParams.practiceAreaCodes &&
      requestParams.practiceAreaCodes.length > 0
    ) {
      requestParams.practiceAreaCodes.forEach((code) => {
        params = params.append('practiceAreaCodes', code);
      });
    }

    if (requestParams.sortBy) {
      params = params.set('sortBy', requestParams.sortBy);
    }

    if (requestParams.sortDirection) {
      params = params.set('sortDirection', requestParams.sortDirection);
    }

    if (requestParams.statuses && requestParams.statuses.length > 0) {
      requestParams.statuses.forEach((status) => {
        params = params.append('statuses', status);
      });
    }

    if (requestParams.requestTypes && requestParams.requestTypes.length > 0) {
      requestParams.requestTypes.forEach((type) => {
        params = params.append('requestTypes', type);
      });
    }

    if (requestParams.priority) {
      params = params.set('priority', requestParams.priority);
    }

    if (requestParams.dueDateFrom) {
      params = params.set(
        'dueDateFrom',
        requestParams.dueDateFrom.toISOString(),
      );
    }

    if (requestParams.dueDateTo) {
      params = params.set('dueDateTo', requestParams.dueDateTo.toISOString());
    }

    if (requestParams.requestedByUserId) {
      params = params.set(
        'requestedByUserId',
        requestParams.requestedByUserId.toString(),
      );
    }

    return this.http.get<PagedResult<RequestDto>>(this.apiUrl, { params });
  }

  /**
   * Export the filtered request list to an Excel workbook. Takes the same
   * filters and sort as getRequests; paging is omitted because the export
   * returns every matching request. exportType picks the workbook layout:
   * 'grouped' (parent row + nested assignment rows) or 'flattened' (one row
   * per assigned matter).
   */
  exportRequests(
    requestParams: RequestListRequest,
    exportType: RequestExportLayout,
  ): Observable<HttpResponse<Blob>> {
    const body: Record<string, unknown> = { exportType };

    if (requestParams.search) {
      body['search'] = requestParams.search;
    }
    if (requestParams.statuses && requestParams.statuses.length > 0) {
      body['statuses'] = requestParams.statuses;
    }
    if (requestParams.requestTypes && requestParams.requestTypes.length > 0) {
      body['requestTypes'] = requestParams.requestTypes;
    }
    if (
      requestParams.practiceAreaCodes &&
      requestParams.practiceAreaCodes.length > 0
    ) {
      body['practiceAreaCodes'] = requestParams.practiceAreaCodes;
    }
    if (requestParams.sortBy) {
      body['sortBy'] = requestParams.sortBy;
    }
    if (requestParams.sortDirection) {
      body['sortDirection'] = requestParams.sortDirection;
    }

    return this.http.post(`${this.apiUrl}/export`, body, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  getRequestById(id: number): Observable<RequestDto> {
    return this.http.get<RequestDto>(`${this.apiUrl}/${id}`);
  }

  getUniqueStatuses(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/statuses`);
  }

  getUniqueRequestTypes(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/request-types`);
  }

  getRequestStats(): Observable<{
    new: number;
    pending: number;
    fulfilled: number;
  }> {
    return this.http.get<{
      new: number;
      pending: number;
      fulfilled: number;
    }>(`${this.apiUrl}/stats`);
  }

  createRequest(request: CreateRequestRequest): Observable<RequestDto> {
    return this.http.post<RequestDto>(this.apiUrl, request);
  }

  updateRequest(
    id: number,
    request: UpdateRequestRequest,
  ): Observable<RequestDto> {
    return this.http.put<RequestDto>(`${this.apiUrl}/${id}`, request);
  }

  deleteRequest(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Update the assignment type category for an assigned resource on a request
   * @param requestId The request ID
   * @param employeeId The employee ID
   * @param assignmentTypeCategory The new assignment type category (PRIMARY or COVERAGE)
   * @returns Observable of the updated request
   */
  updateAssignmentType(
    requestId: number,
    employeeId: string,
    assignmentTypeCategory: string,
  ): Observable<RequestDto> {
    return this.http.put<RequestDto>(
      `${this.apiUrl}/${requestId}/assignment-type`,
      { employeeId, assignmentTypeCategory },
    );
  }

  /**
   * Update the status of a request (e.g., mark as Fulfilled)
   * @param requestId The request ID
   * @param status The new status code (NEW, PENDING, FULFILLED)
   * @returns Observable of the updated request
   */
  updateRequestStatus(
    requestId: number,
    status: string,
  ): Observable<RequestDto> {
    return this.http.put<RequestDto>(`${this.apiUrl}/${requestId}/status`, {
      status,
    });
  }

}
